import { Box, Paper, Typography } from '@mui/material';
import { DataGrid, esES, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { colors } from 'Constants/Colors';
import type { ReportsPaginationI } from './types';

export interface ReportsDataGridProps<T extends { id: string | number }> {
  columns: GridColDef[];
  rows: T[];
  loading?: boolean;
  error?: string | null;
  pagination: ReportsPaginationI | null;
  onPageChange: (page: number) => void; // 1-based
  onRowsPerPageChange: (limit: number) => void;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T) => string;
  noDataMessage?: string;
  pageSizeOptions?: number[];
}

function ReportsDataGrid<T extends { id: string | number }>({
  columns,
  rows,
  loading = false,
  error = null,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  getRowId,
  noDataMessage = 'No hay datos disponibles',
  pageSizeOptions = [15, 25, 50],
}: ReportsDataGridProps<T>) {
  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" component="div">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" component="div">
          {noDataMessage}
        </Typography>
      </Paper>
    );
  }

  const page = pagination ? pagination.page - 1 : 0;
  const pageSize = pagination?.pageSize ?? pageSizeOptions[0];
  const rowCount = pagination?.totalItems ?? rows.length;

  const localeText = (
    esES as unknown as {
      components: { MuiDataGrid: { defaultProps: { localeText: Record<string, string> } } };
    }
  ).components.MuiDataGrid.defaultProps.localeText;

  return (
    <Paper elevation={0} sx={{ width: '100%' }}>
      <Box
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row': {
            cursor: onRowClick ? 'pointer' : 'default',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: colors.custom.table.row.hover,
          },
        }}
      >
        <DataGrid
          autoHeight
          density="compact"
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={getRowId}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          onPageChange={newPage => onPageChange((newPage as number) + 1)}
          onPageSizeChange={newSize => onRowsPerPageChange(newSize as number)}
          rowsPerPageOptions={pageSizeOptions}
          disableSelectionOnClick
          disableColumnMenu
          onRowClick={onRowClick ? (params: GridRowParams) => onRowClick(params.row as T) : undefined}
          localeText={localeText}
        />
      </Box>
    </Paper>
  );
}

export default ReportsDataGrid;
